import {
  type DirectoryNode,
  type FileSystemNode,
  isDirectory,
  isFile,
} from "./fileSystemNode";

/**
 * Escape the Git-wildmatch metacharacters in a literal path so the emitted
 * pattern matches the path itself. Only `[` and `]` are escaped, mirroring the
 * SDK's `path_to_pattern` (`packages/roboto/src/roboto/paths.py`): an
 * unescaped bracket pair parses as a character class, so a literal path
 * containing brackets would never match itself. `*` and `?` stay unescaped —
 * as wildcards they still match their own literal character, so a path
 * containing them matches itself (along with anything else the wildcard
 * happens to match).
 */
function escapeLiteralPath(path: string): string {
  return path.replaceAll("[", "\\[").replaceAll("]", "\\]");
}

/**
 * Recursive `dir/**` Git-wildmatch glob for a selected directory. A
 * directory's member files aren't known on the client, so it travels as a
 * pattern for the server to expand. `relative_path` is already
 * dataset-relative (no leading or trailing slash); `[` and `]` in the name are
 * escaped so the prefix reads as the literal directory, not a character class
 * (see {@link escapeLiteralPath}).
 */
function directoryToRecursivePattern(node: DirectoryNode): string {
  return `${escapeLiteralPath(node.directory.relative_path)}/**`;
}

/** File ids for the explicitly-selected files (directories are skipped). */
export function selectedItemsToFileIds(items: FileSystemNode[]): string[] {
  return items.filter(isFile).map((node) => node.file.file_id);
}

/**
 * Recursive `dir/**` patterns for the selected directories; files are skipped.
 * The session flows pair these with the ids from {@link selectedItemsToFileIds},
 * keeping file ids and directory patterns as separate inputs.
 */
export function selectedDirectoriesToPatterns(
  items: FileSystemNode[],
): string[] {
  return items.filter(isDirectory).map(directoryToRecursivePattern);
}

/**
 * Git-wildmatch path patterns for a whole selection: each file as its
 * `relative_path` (with `[` and `]` escaped so the pattern matches the literal
 * path — see {@link escapeLiteralPath}), each directory as a recursive
 * `dir/**` glob, in selection order. Overlapping selections (a file inside a
 * selected directory, or nested directories) emit one pattern per item — no
 * dedupe or subsumption.
 */
export function selectedItemsToPatterns(items: FileSystemNode[]): string[] {
  return items.flatMap((node) => {
    if (isFile(node)) {
      return escapeLiteralPath(node.file.relative_path);
    }
    if (isDirectory(node)) {
      return directoryToRecursivePattern(node);
    }
    // FileSystemNode isn't a closed union, so TS can't rule this branch out;
    // drop the node rather than emit an empty pattern.
    return [];
  });
}
